import { useEffect, useRef } from 'react';
import { Z_INDEX } from '../../constants';
import { SHOWREEL_ITEMS, type ShowreelItem } from '../../content/showreel';

type ShowreelModalProps = {
  item: ShowreelItem;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
};

export function ShowreelModal({ item, onClose, onPrev, onNext }: ShowreelModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const index = SHOWREEL_ITEMS.findIndex((entry) => entry.id === item.id);
  const positionLabel =
    index >= 0 ? `${index + 1} / ${SHOWREEL_ITEMS.length}` : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.src = item.src;
    if (item.poster) video.poster = item.poster;
    void video.play().catch(() => {});
    return () => {
      video.pause();
      video.removeAttribute('src');
      video.load();
    };
  }, [item]);

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/90 p-3 sm:p-8"
      style={{ zIndex: Z_INDEX.modal }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="showreel-modal-title"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-[1100px] flex flex-col gap-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-baseline gap-3">
            <h2
              id="showreel-modal-title"
              className="font-headline text-[clamp(18px,2vw,28px)] text-[#f2efea] m-0 leading-tight truncate"
            >
              {item.title}
            </h2>
            {positionLabel ? (
              <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-white/40 shrink-0">
                {positionLabel}
              </span>
            ) : null}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close video"
            className="shrink-0 font-mono text-[11px] tracking-[0.16em] uppercase text-white/50 hover:text-primary border border-white/15 hover:border-primary/50 px-3 py-2 transition-colors"
          >
            Close
          </button>
        </div>

        <div className="relative w-full bg-black aspect-video overflow-hidden">
          <video
            ref={videoRef}
            className="block w-full h-full object-contain bg-black"
            controls
            playsInline
            preload="auto"
            poster={item.poster}
          />

          <button
            type="button"
            onClick={onPrev}
            aria-label="Previous video"
            className="absolute left-2 top-1/2 -translate-y-1/2 size-10 rounded-full grid place-items-center text-white/80 hover:text-white border border-white/20 hover:border-white/45 bg-black/45 hover:bg-black/65 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M15 18 9 12l6-6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onNext}
            aria-label="Next video"
            className="absolute right-2 top-1/2 -translate-y-1/2 size-10 rounded-full grid place-items-center text-white/80 hover:text-white border border-white/20 hover:border-white/45 bg-black/45 hover:bg-black/65 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
